import React, { useRef, useEffect } from 'react';
import '../../styles/Home.css';
import { logger } from '../../utils/logger';
import EventsSection from '../common/EventsSection';
import { useAuth } from '../../context/AuthContext';
import { useAnalytics, useScrollTracking } from '../../hooks/useAnalytics';
import { TrackedElement } from '../analytics/TrackedComponents';

const Home = (): React.ReactElement => {
  const { currentUser } = useAuth();
  const { trackPageView, trackCustomEvent } = useAnalytics();
  useScrollTracking(); // Automatic scroll tracking
  
  const introSectionRef = useRef<HTMLElement | null>(null);
  const eventSectionRef = useRef<HTMLElement | null>(null);
  const logoRef = useRef<HTMLVideoElement | null>(null);
  
  // Function to check if elements are in viewport to apply fade effect
  const checkFade = () => {
    const elements = [introSectionRef.current, eventSectionRef.current];
    
    elements.forEach((element: HTMLElement | null) => {
      if (element) {
        const elementTop = element.getBoundingClientRect().top;
        const windowHeight = window.innerHeight;
        
        if (elementTop < windowHeight - 100) {
          element.classList.add('active');
        }
      }
    });
  };
    // Set up fade effect on scroll and logo animation
  useEffect(() => {
    // Track page view
    trackPageView();
    
    // Track home page load event
    trackCustomEvent('page_load', {
      page_name: 'home',
      page_title: 'Home',
      user_type: currentUser ? 'authenticated' : 'anonymous'
    });
    
    // Initial check
    checkFade();
    
    // Add scroll event listener
    window.addEventListener('scroll', checkFade);
    
    // Log logo animation start
    logger.info('🎭 Hero logo animation initiated', { component: 'Home' });
    
    // Clean up
    return () => {
      window.removeEventListener('scroll', checkFade);
    };
  }, [trackPageView, trackCustomEvent, currentUser]);
  
  return (
    <>      
    
    <div className="hero">
        <div 
          className="hero-overlay"
        ></div>        
        <div className="hero-content">          <video 
            ref={logoRef}
            src="/images/logo_neon_video.mp4" 
            className="hero-logo" 
            autoPlay
            muted
            playsInline
            title="BodySharing Logo"
            aria-label="BodySharing Logo Animation"
            onLoadedData={() => {
              logger.success('🎭 Hero logo video loaded', { component: 'Home' });
              trackCustomEvent('video_loaded', {
                video_type: 'hero_logo',
                page_name: 'home'
              });
            }}
          />
          <h1 className="hero-title">Welcome to BodySharing</h1>
          <p className="hero-subtitle">Consent is sexy</p>
          <div className="hero-buttons">
            {!currentUser && (
              <TrackedElement
                as="a"
                href="/sign-in" 
                className="hero-cta-button"
                trackingType="hero_cta"
                trackingData={{
                  button_type: 'cta',
                  button_text: 'SIGN IN',
                  page_location: 'hero',
                  user_type: 'anonymous'
                }}
              >
                SIGN IN
              </TrackedElement>
            )}
            <TrackedElement
              as="a"
              href="/consent" 
              className="hero-secondary-button"
              trackingType="hero_secondary"
              trackingData={{
                button_type: 'secondary',
                button_text: 'CONSENT & GUIDELINES',
                page_location: 'hero',
                user_type: currentUser ? 'authenticated' : 'anonymous'
              }}
            >
              CONSENT & GUIDELINES
            </TrackedElement>
          </div>
        </div>
      </div>
      
      
         
      <EventsSection ref={eventSectionRef} className="fade-in" />
    </>
  );
};

export default Home;
